// Redesigned GeneralInformation component
// Created as part of ENG2-1711: UI Polish

import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Button,
  TextField,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPen,
  faCheck,
  faTimes,
  faCopy,
  faHandshakeSlash,
  faHandshake,
  faTrashAlt,
  faCalendarXmark,
  faLink,
} from '@fortawesome/free-solid-svg-icons'
import { useParams, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import moment, { Moment } from 'moment'
import { FormattedMessage } from 'react-intl'

import { styles } from './general-information.styles'
import { tokens } from 'styles/tokens'
import { useCustomerData } from 'services/queries/use-customer-data'
import { useRisikaUsers } from 'services/queries/use-risika-users'
import { useUpdateCustomer } from 'services/queries/use-update-customer'
import { useUpdateCustomerSingle } from 'services/queries/use-update-customer-single'
import { useRisikaRefreshTokenWeb } from 'services/queries/use-risika-refresh-token'
import { useDeleteCustomer } from 'services/queries/use-delete-customer'
import { useCreateUsers } from 'services/queries/use-create-users'
import { useGetIntegrationsForCustomer } from 'services/queries/use-get-integrations-for-customer'
import { useCreateEconomicIntegration } from 'services/queries/use-create-economic-integration'
import { copyToClipboard } from 'services/helpers/local-id'
import { ActionDialog, AreYouSure, AddBulkUsers } from 'components'
import intl from 'localization/components'
import { derivePlanOptions } from './plan-options'
import { planLabel } from 'utils/plan-label'
import ApplyDefaultsDialog from './apply-defaults-dialog'
import {
  buildApplyDefaultsPayload,
  needsApplyDefaultsPrompt,
} from './apply-package-defaults'

interface GeneralInfoRedesignedProps {
  planNames: string[]
  customerData: any
  customerPlanData: any
}

// Company Info Card Component
const CompanyInfoCard: React.FC = () => {
  const { id: selectedCustomer } = useParams()
  const { data } = useCustomerData(selectedCustomer)
  const { enqueueSnackbar } = useSnackbar()

  const handleCopy = (value: string, label: string) => {
    copyToClipboard(value)
    enqueueSnackbar(`${label} copied to clipboard`, {
      variant: 'success',
      autoHideDuration: 2000,
    })
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never'
    return moment(dateStr).format('DD.MM.YYYY')
  }

  const infoItems = [
    { label: 'Name', value: data?.name, copyable: true },
    { label: 'ID', value: data?.id, copyable: true },
    { label: 'Local ID', value: data?.local_id, copyable: true },
    { label: 'Created', value: formatDate(data?.created_at), copyable: false },
    { label: 'Expires', value: formatDate(data?.expires_at), copyable: false },
  ]

  return (
    <Box sx={styles.companyCard}>
      <Typography sx={styles.companyTitle}>Company</Typography>
      {infoItems.map((item) => (
        <Box key={item.label} sx={styles.infoItem}>
          <Typography sx={styles.infoLabel}>{item.label}</Typography>
          <Box sx={styles.infoValue}>
            <span>{item.value || '-'}</span>
            {item.copyable && item.value && (
              <IconButton
                size="small"
                sx={styles.copyButton}
                onClick={() => handleCopy(String(item.value), item.label)}
              >
                <FontAwesomeIcon icon={faCopy} style={{ fontSize: '12px' }} />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

// Settings Section Component
const SettingsSection: React.FC<{
  planNames: string[]
  customerData: any
  customerPlanData: any
}> = ({ planNames, customerData, customerPlanData }) => {
  const { enqueueSnackbar } = useSnackbar()
  const risikaUsersReq = useRisikaUsers()
  const { mutate: updateCustomer, isLoading: isUpdating } = useUpdateCustomer()
  const { mutate: updateCustomerSingle, isLoading: isUpdatingExpiry } =
    useUpdateCustomerSingle()

  const [isEditing, setIsEditing] = useState(false)
  const [plan, setPlan] = useState(customerPlanData?.subscription_plan || '')
  const [responsibleId, setResponsibleId] = useState(
    customerData?.responsible_user_id || '',
  )
  const [expiresAt, setExpiresAt] = useState<Moment | null>(
    customerData?.expires_at ? moment(customerData.expires_at) : null,
  )

  // Plan options shown in the change-plan dropdown. The derivation rule is
  // documented next to derivePlanOptions in plan-options.ts and pinned by
  // plan-options.test.ts.
  const planOptions = useMemo(
    () => derivePlanOptions(planNames, customerPlanData?.subscription_plan),
    [planNames, customerPlanData?.subscription_plan],
  )

  useEffect(() => {
    if (customerPlanData?.subscription_plan) {
      setPlan(customerPlanData.subscription_plan)
    }
  }, [customerPlanData?.subscription_plan])

  useEffect(() => {
    if (customerData?.responsible_user_id) {
      setResponsibleId(customerData.responsible_user_id)
    }
  }, [customerData?.responsible_user_id])

  // ENG2-2455 (T4): moving a customer onto a 2026 package first asks whether
  // to reset its overrides to the package defaults (see
  // apply-package-defaults.ts). The plan being confirmed is held here while
  // the dialog is open.
  const [pendingPlan, setPendingPlan] = useState<string | null>(null)

  const submitSettings = (
    data: Record<string, unknown>,
    successMessage: string,
  ) => {
    const body: any = { data }
    if (responsibleId !== customerData?.responsible_user_id) {
      body.responsible_user_id = responsibleId
    }
    updateCustomer(
      { reqBody: body, companyId: customerData.id.toString() },
      {
        onSuccess: () => {
          enqueueSnackbar(successMessage, { variant: 'success' })
          setPendingPlan(null)
          setIsEditing(false)
        },
      },
    )
  }

  const handleSave = () => {
    const planChanged = plan !== customerPlanData?.subscription_plan
    if (
      planChanged &&
      needsApplyDefaultsPrompt(customerPlanData?.subscription_plan, plan)
    ) {
      setPendingPlan(plan)
      return
    }
    submitSettings(
      planChanged ? { subscription_plan: plan } : {},
      'Settings updated',
    )
  }

  const handleCancel = () => {
    setPlan(customerPlanData?.subscription_plan || '')
    setResponsibleId(customerData?.responsible_user_id || '')
    setIsEditing(false)
  }

  const handleExpirationChange = (newDate: Moment | null) => {
    setExpiresAt(newDate)
    updateCustomerSingle({
      companyId: customerData.id,
      data: { expires_at: newDate ? newDate.toDate() : null },
    })
  }

  const handleNeverExpires = () => {
    setExpiresAt(null)
    updateCustomerSingle({
      companyId: customerData.id,
      data: { expires_at: null },
    })
  }

  const risikaUsers = risikaUsersReq.data?.data || []

  return (
    <Box sx={styles.settingsRow}>
      <ApplyDefaultsDialog
        open={pendingPlan !== null}
        plan={pendingPlan ?? ''}
        isSaving={isUpdating}
        onApply={() =>
          pendingPlan &&
          submitSettings(
            buildApplyDefaultsPayload(pendingPlan),
            'Plan changed and package defaults applied',
          )
        }
        onJustChange={() =>
          pendingPlan &&
          submitSettings({ subscription_plan: pendingPlan }, 'Settings updated')
        }
        onCancel={() => setPendingPlan(null)}
      />
      {/* Plan Select */}
      <Box sx={styles.fieldContainer}>
        <Typography sx={styles.fieldLabel}>Plan</Typography>
        <Select
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          disabled={!isEditing}
          size="small"
          fullWidth
          sx={styles.select}
        >
          {planOptions.map((p) => (
            <MenuItem key={p} value={p}>
              {planLabel(p)}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Responsible Select */}
      <Box sx={{ ...styles.fieldContainer, minWidth: '200px' }}>
        <Typography sx={styles.fieldLabel}>Responsible</Typography>
        {risikaUsersReq.isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <Select
            value={responsibleId}
            onChange={(e) => setResponsibleId(e.target.value)}
            disabled={!isEditing}
            size="small"
            fullWidth
            sx={styles.select}
          >
            {risikaUsers.map((user: any) => (
              <MenuItem key={user.id} value={user.id}>
                {user.email}
              </MenuItem>
            ))}
          </Select>
        )}
      </Box>

      {/* Edit/Save/Cancel Buttons */}
      {!isEditing ? (
        <Tooltip title="Edit settings">
          <IconButton sx={styles.editButton} onClick={() => setIsEditing(true)}>
            <FontAwesomeIcon icon={faPen} style={{ fontSize: '14px' }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Save changes">
            <IconButton
              sx={{
                ...styles.editButton,
                backgroundColor: tokens.colors.success[50],
                borderColor: tokens.colors.success[300],
                color: tokens.colors.success[600],
                '&:hover': {
                  backgroundColor: tokens.colors.success[100],
                },
              }}
              onClick={handleSave}
              disabled={isUpdating}
            >
              <FontAwesomeIcon icon={faCheck} style={{ fontSize: '14px' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton
              sx={{
                ...styles.editButton,
                backgroundColor: tokens.colors.error[50],
                borderColor: tokens.colors.error[300],
                color: tokens.colors.error[600],
                '&:hover': {
                  backgroundColor: tokens.colors.error[100],
                },
              }}
              onClick={handleCancel}
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '14px' }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Divider */}
      <Box
        sx={{ width: '1px', height: '40px', backgroundColor: 'divider', mx: 1 }}
      />

      {/* Expiration Date */}
      <Box sx={styles.fieldContainer}>
        <Typography sx={styles.fieldLabel}>Expiration Date</Typography>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            format="DD.MM.YYYY"
            value={expiresAt}
            onChange={handleExpirationChange}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                sx: styles.datePicker,
              },
            }}
          />
        </LocalizationProvider>
      </Box>

      {/* Never Expires Button - compact icon button */}
      <Tooltip title="Set to never expire">
        <IconButton
          onClick={handleNeverExpires}
          disabled={isUpdatingExpiry || !expiresAt}
          sx={styles.editButton}
        >
          <FontAwesomeIcon
            icon={faCalendarXmark}
            style={{ fontSize: '14px' }}
          />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

// Actions Section Component
const ActionsSection: React.FC<{ customerData: any }> = ({ customerData }) => {
  const { id: selectedCustomer } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  // API Token
  const {
    data: tokenData,
    refetch: refetchToken,
    isLoading: isLoadingToken,
  } = useRisikaRefreshTokenWeb(customerData?.id)

  useEffect(() => {
    if (!tokenData) return
    if (tokenData.success === true) {
      copyToClipboard(tokenData.data)
      enqueueSnackbar('API token copied to clipboard', { variant: 'success' })
    }
    if (tokenData.success === false) {
      enqueueSnackbar(tokenData.error?.name || 'Failed to get token', {
        variant: 'error',
      })
    }
  }, [tokenData, enqueueSnackbar])

  // Revoke Access
  const { mutate: updateCustomer, isLoading: isUpdatingRevoke } =
    useUpdateCustomer()
  const [isRevokeConfirmOpen, setIsRevokeConfirmOpen] = useState(false)
  const isRevoked = customerData?.is_revoked

  const handleRevoke = () => {
    updateCustomer({
      reqBody: { data: {}, is_revoked: !isRevoked },
      companyId: selectedCustomer?.toString() || '',
    })
    setIsRevokeConfirmOpen(false)
  }

  // Delete Company
  const { mutate: deleteCustomer, isLoading: isDeleting } = useDeleteCustomer()
  const [deleteInputValue, setDeleteInputValue] = useState('')

  const handleDeleteCompany = () => {
    if (deleteInputValue === customerData?.name) {
      deleteCustomer(selectedCustomer || '')
      navigate('/')
      enqueueSnackbar('Company deleted successfully', { variant: 'success' })
    } else {
      enqueueSnackbar("Company name doesn't match", { variant: 'error' })
    }
  }

  // Add Users
  const { mutate: createUsers } = useCreateUsers()
  const [newUsers, setNewUsers] = useState([{ userName: '', email: '' }])

  const handleCreateUsers = () => {
    const transformedData = newUsers.map((user) => ({
      name: user.userName,
      email: user.email,
      customer_id: Number(selectedCustomer),
    }))
    createUsers(transformedData)
  }

  // Economic Integration
  const { mutate: createEconomicIntegration } = useCreateEconomicIntegration()
  const { data: integrations } = useGetIntegrationsForCustomer(selectedCustomer)
  const [economicKey, setEconomicKey] = useState('')

  const economicIntegration = integrations?.find(
    (i: any) => i.type === 'economic',
  )

  useEffect(() => {
    if (economicIntegration) {
      setEconomicKey(economicIntegration.token)
    }
  }, [economicIntegration])

  const handleCreateIntegration = () => {
    createEconomicIntegration({
      customerId: selectedCustomer,
      economicToken: economicKey,
    })
  }

  return (
    <Box sx={styles.actionsSection}>
      <Typography sx={styles.actionsSectionTitle}>Quick Actions</Typography>

      <Box sx={styles.actionsRow}>
        {/* Copy API Token */}
        <Button
          variant="contained"
          size="small"
          onClick={() => refetchToken()}
          disabled={isLoadingToken}
          sx={[styles.actionButton, styles.primaryAction]}
        >
          <FontAwesomeIcon
            icon={faCopy}
            style={styles.buttonIcon as React.CSSProperties}
          />
          {isLoadingToken ? 'Loading...' : 'Copy API Token'}
        </Button>

        {/* Add Users */}
        <AddBulkUsers
          users={newUsers}
          setUsers={setNewUsers}
          action={handleCreateUsers}
        />

        {/* Revoke/Grant Access */}
        <Button
          variant="contained"
          size="small"
          onClick={() => setIsRevokeConfirmOpen(true)}
          disabled={isUpdatingRevoke}
          sx={[
            styles.actionButton,
            isRevoked ? styles.successAction : styles.warningAction,
          ]}
        >
          <FontAwesomeIcon
            icon={isRevoked ? faHandshake : faHandshakeSlash}
            style={styles.buttonIcon as React.CSSProperties}
          />
          {isRevoked ? 'Grant Access' : 'Revoke Access'}
        </Button>

        {/* Delete Company */}
        <ActionDialog
          disabled={deleteInputValue !== customerData?.name}
          title={
            <FormattedMessage
              id={intl.customerManagement('delete-company-dialog-title')}
            />
          }
          content={() => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography>
                This action cannot be undone! This will permanently delete{' '}
                <strong>{customerData?.name}</strong> from our database.
              </Typography>
              <Typography>
                Please type <strong>{customerData?.name}</strong> to confirm.
              </Typography>
              <TextField
                value={deleteInputValue}
                onChange={(e) => setDeleteInputValue(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Type company name to confirm"
              />
            </Box>
          )}
          action={handleDeleteCompany}
          maxWidth="sm"
          fullWidth
          render={(open: () => void) => (
            <Button
              variant="contained"
              size="small"
              onClick={open}
              disabled={isDeleting}
              sx={[styles.actionButton, styles.dangerAction]}
            >
              <FontAwesomeIcon
                icon={faTrashAlt}
                style={styles.buttonIcon as React.CSSProperties}
              />
              Delete Company
            </Button>
          )}
          agreeText="Delete"
        />
      </Box>

      {/* Integration Section */}
      <Box sx={styles.integrationSection}>
        <Box sx={styles.fieldContainer}>
          <Typography sx={styles.fieldLabel}>E-conomic Integration</Typography>
          {economicIntegration ? (
            <Tooltip title={economicKey}>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: tokens.colors.grey[600],
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '200px',
                }}
              >
                {economicKey}
              </Typography>
            </Tooltip>
          ) : (
            <TextField
              value={economicKey}
              onChange={(e) => setEconomicKey(e.target.value)}
              placeholder="Enter E-conomic key..."
              size="small"
              sx={styles.integrationInput}
            />
          )}
        </Box>
        <Tooltip
          title={
            economicIntegration
              ? 'Integration already exists'
              : 'Creates an extra user for this company'
          }
        >
          <span>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCreateIntegration}
              disabled={!!economicIntegration || economicKey.length < 10}
              sx={[styles.actionButton, styles.secondaryAction]}
            >
              <FontAwesomeIcon
                icon={faLink}
                style={styles.buttonIcon as React.CSSProperties}
              />
              Create Integration
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Revoke Confirmation Dialog */}
      <AreYouSure
        open={isRevokeConfirmOpen}
        setOpen={setIsRevokeConfirmOpen}
        onSubmit={handleRevoke}
        title={isRevoked ? 'Grant Access' : 'Revoke Access'}
        content={
          isRevoked
            ? 'Are you sure you want to grant access back to this company?'
            : 'Are you sure you want to revoke access for this company?'
        }
      />
    </Box>
  )
}

// Main Component
const GeneralInfoRedesigned: React.FC<GeneralInfoRedesignedProps> = ({
  planNames,
  customerData,
  customerPlanData,
}) => {
  return (
    <Box sx={styles.container}>
      {/* Left: Company Info */}
      <CompanyInfoCard />

      {/* Right: Settings and Actions */}
      <Box sx={styles.rightContent}>
        <SettingsSection
          planNames={planNames}
          customerData={customerData}
          customerPlanData={customerPlanData}
        />
        <ActionsSection customerData={customerData} />
      </Box>
    </Box>
  )
}

export default GeneralInfoRedesigned
